import React, { ReactNode } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PermissionGate from '../../../../utils/hooks/PermissionGate';

export interface MenuItemType<T> {
  label: string;
  icon: ReactNode;
  permission: string[];
  onClick: (row: T) => void;
}

interface ContextMenuProps<T> {
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  row: T;
  menuItems: MenuItemType<T>[];
}

/**
 * Renders a context menu using standard Material UI Menu.
 */
export function ContextMenu<T>({
  anchorEl,
  onClose,
  row,
  menuItems,
}: Readonly<ContextMenuProps<T>>) {
  const isOpen = Boolean(anchorEl);

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      PaperProps={{
        elevation: 3,
        sx: {
          minWidth: 170,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          p: 0.5,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      {menuItems.map((item) => (
        <PermissionGate requiredPermissions={item.permission} key={item.label}>
          <MenuItem
            onClick={() => {
              item.onClick(row);
              onClose();
            }}
            sx={{
              borderRadius: '8px',
              py: 1,
              px: 1.5,
              fontSize: '0.8125rem',
              fontWeight: 500,
              gap: 1.25,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 'auto !important', color: 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.primary',
              }}
            />
          </MenuItem>
        </PermissionGate>
      ))}
    </Menu>
  );
}

